import React, { useState } from 'react';
import { trackEvent } from '../lib/analytics';

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    trackEvent('form_start', { form_id: 'contact', form_name: 'Contact Us' });

    // Validate Turnstile token exists
    if (!turnstileToken) {
      setStatus('error');
      trackEvent('form_error', { form_id: 'contact', error_type: 'captcha_missing' });
      console.error('Turnstile verification required');
      return;
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          turnstileToken: turnstileToken
        })
      });

      if (response.ok) {
        setStatus('success');
        // generate_lead is GA4's recommended event for a completed enquiry, so
        // it lands in the standard reports rather than only in a custom one.
        trackEvent('generate_lead', { form_id: 'contact', form_name: 'Contact Us' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
        // The status code is useful; the response body may quote user input.
        trackEvent('form_error', { form_id: 'contact', error_type: 'server', status_code: response.status });
      }
    } catch (error) {
      console.error(
        'Error submitting form:',
        error && error.message ? error.message : 'Unknown error'
      );
      setStatus('error');
      trackEvent('form_error', { form_id: 'contact', error_type: 'network' });
    }
  };

  React.useEffect(() => {
    let widgetId;

    // Define callback for Turnstile
    const onSuccess = (token) => {
      setTurnstileToken(token);
    };

    // Explicitly render Turnstile widget
    const renderWidget = () => {
      if (window.turnstile && !widgetId) {
        const container = document.querySelector('.cf-turnstile');
        if (container && !container.innerHTML) {
          widgetId = window.turnstile.render(container, {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA',
            callback: onSuccess,
            theme: 'light'
          });
        }
      }
    };

    // Wait for Turnstile API to load
    if (window.turnstile) {
      renderWidget();
    } else {
      // Turnstile script not loaded yet, wait for it
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          renderWidget();
        }
      }, 100);

      return () => {
        clearInterval(checkTurnstile);
        if (widgetId !== undefined && window.turnstile) {
          window.turnstile.remove(widgetId);
        }
      };
    }

    // Cleanup on unmount
    return () => {
      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, []);

  return (
    <section id="contact" className="bg-white dark:bg-gray-900">
      <div className="py-8 lg:py-16 px-4 mx-auto max-w-screen-md">
        <h2 className="mb-4 text-4xl tracking-tight font-extrabold text-center text-gray-900 dark:text-white">
          Contact Us
        </h2>
        <p className="mb-8 lg:mb-16 font-light text-center text-gray-500 dark:text-gray-400 sm:text-xl">
          Have a question or need assistance? Get in touch with our team and we'll respond as soon as possible.
        </p>

        {status === 'success' && (
          <div className="mb-4 p-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400" role="alert">
            <span className="font-medium">Success!</span> Your message has been sent successfully.
          </div>
        )}

        {status === 'error' && (
          <div className="mb-4 p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
            <span className="font-medium">Error!</span> There was a problem sending your message. Please try again.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
              Your name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-accent-500 dark:focus:border-accent-500 dark:shadow-sm-light"
              placeholder="John Doe"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
              Your email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-accent-500 dark:focus:border-accent-500 dark:shadow-sm-light"
              placeholder="name@example.com"
              required
            />
          </div>
          <div>
            <label htmlFor="subject" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
              Subject
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="block p-3 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-accent-500 dark:focus:border-accent-500 dark:shadow-sm-light"
              placeholder="How can we help you?"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="message" className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-400">
              Your message
            </label>
            <textarea
              id="message"
              name="message"
              rows="6"
              value={formData.message}
              onChange={handleChange}
              className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-accent-500 dark:focus:border-accent-500"
              placeholder="Leave a comment..."
              required
            ></textarea>
          </div>

          {/* Cloudflare Turnstile - widget rendered via JavaScript */}
          <div className="cf-turnstile"></div>

          <button
            type="submit"
            disabled={status === 'sending'}
            className="py-3 px-5 text-sm font-semibold text-center text-white rounded-lg sm:w-fit bg-gradient-to-r from-accent-500 to-accent-700 hover:from-accent-600 hover:to-accent-800 focus:ring-4 focus:outline-none focus:ring-accent-300 dark:focus:ring-accent-900 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100"
          >
            {status === 'sending' ? 'Sending...' : 'Send message'}
          </button>
        </form>
      </div>
    </section>
  );
}

export default ContactForm;
